import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Icons,
  Input,
  InputNumber,
  ListButton,
  Modal,
  Popconfirm,
  Row,
  Show,
  Table,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan, useNavigation, useOne, useQuery } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { EReturnStatus } from 'enums';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { IReturn } from 'interfaces';
import { useState } from 'react';
import { createFinalStockOut, getFinalStockOuts } from 'services/final-stock-out';
import { deleteReturn } from 'services/return';
import {
  finalStockOutFeatureFlag,
  remainingToWriteOff,
  shouldShowFinalStockOutAction,
  writtenOffQuantityByProduct,
} from 'utilities/final-stock-out';

type WriteOffTarget = {
  productVariantId: string;
  name?: string;
  remaining: number;
};

const { Title, Text } = Typography;

export const ReturnShow = () => {
  const { list } = useNavigation();
  const { id } = useParams();
  const { data, isLoading } = useOne<IReturn>({
    resource: 'returnCopy',
    id: id || '',
    metaData: {
      fields: [
        'id',
        'returnDate',
        'returnStatus',
        'totalAmount',
        'note',
        {
          user: ['id', 'name'],
        },
        {
          client: ['id', 'name'],
        },
        {
          warehouse: ['id', 'name'],
        },
        {
          salesCopy: ['id'],
        },
        {
          returnProductsCopy: [
            'id',
            'returnQuantity',
            'lossAmount',
            'subTotal',
            {
              salesProductCopy: [
                'unitPrice',
                {
                  productVariant: ['id', 'sku', 'name'],
                },
              ],
            },
          ],
        },
      ],
    },
  });

  const { data: canAccessDelete } = useCan({
    resource: 'returns',
    action: 'delete',
    params: { field: 'id' },
  });

  // Stitches S6: write returned stock off as a final stock out.
  const finalStockOutEnabled = useFeatureFlag(finalStockOutFeatureFlag);
  const { data: canCreateFinalStockOut } = useCan({
    resource: 'final-stock-outs',
    action: 'create',
  });
  const { data: canReadFinalStockOuts } = useCan({ resource: 'final-stock-outs', action: 'list' });
  const { data: finalStockOuts, refetch: refetchFinalStockOuts } = useQuery(
    ['finalStockOuts', 'return', id],
    () => getFinalStockOuts({ returnCopyId: id, limit: 500 }),
    { enabled: finalStockOutEnabled && canReadFinalStockOuts?.can === true && Boolean(id) },
  );
  const writtenOff = writtenOffQuantityByProduct(finalStockOuts?.data ?? []);
  const [writeOffTarget, setWriteOffTarget] = useState<WriteOffTarget>();
  const [isWritingOff, setIsWritingOff] = useState(false);
  const [writeOffForm] = Form.useForm<{ quantity: number; reason: string }>();

  const record = data?.data;
  const note = record?.note;
  const userName = record?.user?.name ?? 'N/A';
  const warehouseName = record?.warehouse?.name;
  const invoiceId = record?.salesCopy?.id;
  const clientName = record?.client?.name;
  const returnProducts = record?.returnProductsCopy;
  const flattenReturnProducts = returnProducts?.map((product) => {
    return {
      id: product.id,
      productVariantId: product.salesProductCopy?.productVariant?.id,
      writtenOff: writtenOff[product.salesProductCopy?.productVariant?.id ?? ''] ?? 0,
      sku: product.salesProductCopy?.productVariant?.sku,
      name: product.salesProductCopy?.productVariant?.name,
      quantity: product.returnQuantity,
      unitPrice: product.salesProductCopy?.unitPrice,
      lossAmount: product.lossAmount,
      subTotal: product.subTotal,
    };
  });

  const isCompleted = record?.returnStatus === EReturnStatus.COMPLETED;
  const showFinalStockOutAction = shouldShowFinalStockOutAction({
    featureEnabled: finalStockOutEnabled,
    canCreate: canCreateFinalStockOut?.can === true,
    returnStatus: record?.returnStatus,
  });

  const handleWriteOff = async () => {
    if (!writeOffTarget || !record?.warehouse?.id || !id) return;
    const { quantity, reason } = await writeOffForm.validateFields();
    setIsWritingOff(true);
    try {
      await createFinalStockOut({
        productVariantId: writeOffTarget.productVariantId,
        warehouseId: record.warehouse.id,
        returnCopyId: id,
        quantity,
        reason,
      });
      notification.success({
        message: 'Written off',
        description: `${quantity} of ${writeOffTarget.name ?? 'the product'} left stock.`,
      });
      setWriteOffTarget(undefined);
      writeOffForm.resetFields();
      refetchFinalStockOuts();
    } catch (error: any) {
      notification.error({ message: 'Failed', description: error?.message });
    } finally {
      setIsWritingOff(false);
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;

    try {
      await deleteReturn(id);
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a return',
      });
      list('returns');
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete return',
      });
    }
  };

  const pageHeader = !isLoading ? (
    <>
      <ListButton />
      <Popconfirm
        title="Are you sure?"
        okText="Delete"
        onConfirm={() => handleDelete(id)}
        disabled={!canAccessDelete?.can}
      >
        <Button
          size="middle"
          danger
          icon={<Icons.DeleteOutlined />}
          disabled={!canAccessDelete?.can}
          hidden={isCompleted}
        >
          Delete
        </Button>
      </Popconfirm>
    </>
  ) : null;

  return (
    <>
      <Show title="Show Return" isLoading={isLoading} headerProps={{ extra: pageHeader }}>
        {/* Return Info */}
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={18}>
            <Card>
              <Title level={5}>Reported By</Title>
              <Text>{userName}</Text>
              <Title level={5}>Warehouse</Title>
              <Text>{warehouseName}</Text>
              <Title level={5}>Invoice ID</Title>
              <Text>{invoiceId}</Text>
              <Title level={5}>Client</Title>
              <Text>{clientName}</Text>
              <Title level={5}>Note</Title>
              <Text>{note}</Text>
            </Card>
          </Col>
        </Row>

        <Divider />
        {/* Products Table */}
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={18}>
            <Table
              dataSource={flattenReturnProducts || []}
              loading={isLoading}
              rowKey="id"
              scroll={{ x: true }}
            >
              <Table.Column dataIndex="sku" title="SKU" />
              <Table.Column dataIndex="name" title="Product Name" />
              <Table.Column dataIndex="quantity" title="Quantity" />
              <Table.Column dataIndex="subTotal" title="Total Price" />
              {finalStockOutEnabled && canReadFinalStockOuts?.can && (
                <Table.Column dataIndex="writtenOff" title="Written Off" />
              )}
              {showFinalStockOutAction && (
                <Table.Column
                  title="Final Stock Out"
                  render={(_, product: NonNullable<typeof flattenReturnProducts>[number]) => {
                    const remaining = remainingToWriteOff(
                      Number(product.quantity),
                      product.writtenOff,
                    );
                    return (
                      <Button
                        size="small"
                        disabled={!product.productVariantId || remaining === 0}
                        onClick={() =>
                          product.productVariantId &&
                          setWriteOffTarget({
                            productVariantId: product.productVariantId,
                            name: product.name,
                            remaining,
                          })
                        }
                      >
                        Write off
                      </Button>
                    );
                  }}
                />
              )}
            </Table>
          </Col>
        </Row>
      </Show>
      <Modal
        title={`Final stock out: ${writeOffTarget?.name ?? ''}`}
        visible={Boolean(writeOffTarget)}
        okText="Write off"
        confirmLoading={isWritingOff}
        onOk={handleWriteOff}
        onCancel={() => setWriteOffTarget(undefined)}
        destroyOnClose
      >
        <Form form={writeOffForm} layout="vertical" preserve={false}>
          <Form.Item
            label={`Quantity (up to ${writeOffTarget?.remaining ?? 0})`}
            name="quantity"
            initialValue={writeOffTarget?.remaining}
            rules={[{ required: true, message: 'Enter a quantity' }]}
          >
            <InputNumber min={1} max={writeOffTarget?.remaining} precision={0} />
          </Form.Item>
          <Form.Item
            label="Reason"
            name="reason"
            rules={[{ required: true, whitespace: true, message: 'Enter a reason' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
